<script lang="ts">
	import { format } from 'svelte-i18n';
	//services
	import { open_external_link_android } from 'src/services/native_container_requests_android';
	import { open_external_link } from 'src/services/native_container_requests_ios';
	//stores
	import { user_data } from 'src/stores/account/user';
import { os } from 'src/stores/session/native-bridge';
	import { inclusionState } from 'src/stores/features/inclusion';
	//helpers
	import { capFirstChar } from 'src/helpers/utilities';
	//Design system components
	import { Text, Label, Alert } from '@Shelly/shelly-design-system';

	import { isDevServerUser, isT1User } from 'src/helpers/user_server_conditions';

	export let inclusionData;

	const navigateToLink = () => {
		// navigates to the support page and passes the language as first route
		const link = `https://support.shelly.cloud/${
			/^(it|de)$/.test($user_data.lang) ? $user_data.lang : 'en'
		}/support/solutions/articles/103000279888-update-device-and-include-it-manually-to-shelly-smart-control`;

		if ($os === 'IOS') {
			open_external_link(link);
		} else {
			open_external_link_android(link);
		}
	};
</script>

{#if $inclusionState.raw_value === 'authenticationError'}
	<Alert
		type="danger"
		heading={$format('failed_device_authentication_error_message', {
			default:
				"The device cannot be included in the Shelly app because it has authentication enabled. Please use the device's local web interface to unlock it and then try again.",
		})}
	/>
{:else if $inclusionState.raw_value === 'request_digest_password_from_user'}
	<Alert
		type="info"
		heading={$format('failed_alert_enter_auth_code', {
			default:
				'Please enter the correct device authentication code in order to continue the process.',
		})}
	/>
{:else}
	<Text element="h2" class="mb-s"
		>{capFirstChar(
			$format('text_title_inclusion_errors', {
				default: "Can't to include your Shelly device?",
			}),
		)}</Text
	>

	<Text styleType="body-small" class="mb-m"
		>{capFirstChar(
			$format('text_body_inclusion_errors', {
				default: 'Here are few more tips:',
			}),
		)}</Text
	>

	<Alert
		type="warning"
		isHeadingBold
		heading={$format('text_body_inclusion_error_fw_175_heading', {
			default: 'Firmware 1.7.5+ Provisioning Window',
		})}
		message={$format('text_body_inclusion_error_fw_175', {
			default:
				"Devices with <b>firmware 1.7.5</b> and above have a 15-minute provisioning window after being powered on. If the device becomes unreachable, try power cycling it (turn off and on) to restart the <b>'provisioning window'</b>.",
		})}
	/>

	<ul style="padding-inline-start:20px">
		<li class="mb-s">
			<Text styleType="body-small">
				{capFirstChar(
					$format('text_body_inclusion_error_1', {
						default:
							'Make sure your device is in inclusion mode. If it’s a battery-operated device, press the button 3 times to wake it up and put it in inclusion mode.',
					}),
				)}
			</Text>
		</li>

		<li class="mb-s">
			<Text styleType="body-small">
				{capFirstChar(
					$format('text_body_inclusion_error_2', {
						default:
							'Check your Wi-Fi name and password. Pay attention to capital letters and special characters. Make sure auto fill is not used and instead type the credentials and avoid copy/pasting.',
					}),
				)}
			</Text>
		</li>

		<li class="mb-s">
			<Text styleType="body-small">
				{capFirstChar(
					$format('text_body_inclusion_error_wifi_band', {
						default:
							"Make sure your device is connecting to a 2.4 GHz Wi-Fi network. Connections over 5 GHz networks are not supported and may prevent the device from pairing. If you're unsure, check your router settings or switch to a 2.4 GHz network.",
					}),
				)}
			</Text>
		</li>

		<li class="mb-s">
			<Text styleType="body-small">
				{capFirstChar(
					$format('text_body_inclusion_error_3', {
						default:
							'Make sure your device is powered on. If it’s a battery-operated device make sure it’s awake.',
					}),
				)}
			</Text>
		</li>

		<li class="mb-s">
			<Text styleType="body-small">
				{capFirstChar(
					$format('text_body_inclusion_error_4', {
						default:
							'Manually setup the device, learn how to connect and setup the device by following the link below:',
					}),
				)}
			</Text>
			<span
				class="link-styles text-small mb-"
				style="font-size: inherit; line-height: inherit;"
				role="presentation"
				on:click={() => {
					navigateToLink();
				}}
			>
				{capFirstChar(
					$format('text_link_manual_setup', {
						default: 'Manual device setup guidelines.',
					}),
				)}
			</span>
		</li>

		<li>
			<Text styleType="body-small">
				{capFirstChar(
					$format('text_body_inclusion_error_5', {
						default:
							'Make sure all required permissions have been granted in the Phone Settings/Shelly Smart Control App',
					}),
				)}
			</Text>
		</li>
	</ul>

	{#if import.meta.env.DEV || isDevServerUser(['repo', 'nb']) || isT1User()}
		<Label
			icon="fas fa-arrow-down"
			value="Visible to Dev | Test | T1 users"
			type="warning"
			size="m"
			style="padding-right:8px;"
			class="mb-m"
		/>
		<Text
			>{capFirstChar(
				$format('text_body_inclusion_error_code', {
					default: `Error code: ${inclusionData.errorCode} - ${$inclusionState.raw_value}`,
				}),
			)}</Text
		>
	{/if}
{/if}
